/**
 * AG Grid column definitions for Workflows list view.
 * Modernized for Angular 20+ - no moment.js dependencies.
 */

import { dateValueFormatter as dateFormatter } from '../utils/format-date-cell';

function userValueGetter(field: string) {
  return (params: any) => {
    const val = params.data?.[field];
    if (!val) return '';
    if (typeof val === 'string') return val;
    return val.friendlyName || val.email || '';
  };
}

/**
 * "New" badge column — shows a green NEW pill for requests created within
 * the last 7 days. Read-only visual indicator, not sortable/filterable.
 */
export const colIsNew = {
  colId: 'isNew',
  headerName: 'New',
  field: 'createdAt',
  width: 70,
  sortable: false,
  filter: false,
  cellRenderer: (params: any) => {
    const createdAt = params?.value || params?.data?.createdAt || params?.data?.dateRequested;
    if (!createdAt) return '';
    const createdMs = new Date(createdAt).getTime();
    if (isNaN(createdMs)) return '';
    const ageMs = Date.now() - createdMs;
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
    if (ageMs < 0 || ageMs > sevenDaysMs) return '';
    return '<span style="background:#2e7d32;color:#fff;padding:2px 10px;border-radius:999px;font-size:10px;font-weight:700;letter-spacing:0.5px;">NEW</span>';
  }
};

export const colWorkflowNum = {
  colId: 'workflowNum',
  headerName: 'Request ID',
  field: 'workflowNum',
  width: 140,
  filter: 'agTextColumnFilter'
};

export const colName = {
  colId: 'name',
  headerName: 'Name',
  field: 'name',
  width: 200,
  filter: 'agTextColumnFilter'
};

export const colPhase = {
  colId: 'phase',
  headerName: 'Phase',
  field: 'phase',
  width: 200,
  filter: 'agTextColumnFilter'
};

export const colStatus = {
  colId: 'status',
  headerName: 'Status',
  field: 'status',
  width: 130,
  filter: 'agTextColumnFilter'
};

export const colDocumentType = {
  colId: 'documentType',
  headerName: 'Document Type',
  field: 'documentType',
  width: 180,
  filter: 'agTextColumnFilter'
};

export const colTransactionType = {
  colId: 'transactionType',
  headerName: 'Transaction Type',
  field: 'transactionType',
  width: 180,
  filter: 'agTextColumnFilter'
};

export const colPriority = {
  colId: 'priority',
  headerName: 'Priority',
  field: 'priority',
  width: 120,
  filter: 'agTextColumnFilter'
};

export const colRequestor = {
  colId: 'requestor',
  headerName: 'Requestor',
  field: 'requestor',
  width: 150,
  filter: 'agTextColumnFilter',
  valueGetter: userValueGetter('requestor')
};

export const colAssignedTo = {
  colId: 'assignedTo',
  headerName: 'Assigned To',
  field: 'assignedTo',
  width: 150,
  filter: 'agTextColumnFilter',
  valueGetter: userValueGetter('assignedTo')
};

export const colDateRequested = {
  colId: 'dateRequested',
  headerName: 'Date Requested',
  field: 'dateRequested',
  width: 140,
  valueFormatter: dateFormatter,
  filter: 'agDateColumnFilter'
};

// NOTE: `colSubmittedTo` and `colSubmissionDate` were removed. They bound to
// `submittedTo` / `submissionDate`, which are not on WorkflowRequest and are never
// returned by the API — so both rendered permanently blank. `colAssignedTo` and
// `colDateRequested` above are the real equivalents and are what the default column
// set now uses.

export const colDaysInProcess = {
  colId: 'daysInProcess',
  headerName: 'Days in Process',
  field: 'daysInProcess',
  width: 130,
  filter: 'agNumberColumnFilter',
  valueGetter: (params: any) => {
    // `createdAt`, not the non-existent `createdDate`. This worked before only by
    // accident: the bad lookup fell through to `dateRequested`, which the backend
    // happens to set to the same instant on create.
    const created = params.data?.createdAt || params.data?.dateRequested;
    if (!created) return '';
    const start = new Date(created);
    if (isNaN(start.getTime())) return '';
    const now = new Date();
    const diffMs = now.getTime() - start.getTime();
    return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
  }
};

export const colCreatedDate = {
  colId: 'createdDate',
  headerName: 'Created Date',
  // The backend field is `createdAt`; colId stays `createdDate` so any saved view
  // referencing it keeps resolving to this column.
  field: 'createdAt',
  width: 140,
  valueFormatter: dateFormatter,
  filter: 'agDateColumnFilter'
};

export const colDesiredEffectiveDate = {
  colId: 'desiredEffectiveDate',
  // "Desired" was wrong: this is the effective date the AI read out of the uploaded
  // document, not a date the requester asked for.
  headerName: 'Effective Date',
  field: 'extractedEffectiveDate',
  width: 160,
  valueFormatter: dateFormatter,
  filter: 'agDateColumnFilter'
};

export const colDateCompleted = {
  colId: 'dateCompleted',
  headerName: 'Date Completed',
  field: 'dateCompleted',
  width: 140,
  valueFormatter: dateFormatter,
  filter: 'agDateColumnFilter'
};

export const colSourceRecord = {
  colId: 'sourceRecord',
  headerName: 'Source Record',
  field: 'contractFriendlyName',
  width: 180,
  filter: 'agTextColumnFilter'
};

export const colEntityType = {
  colId: 'entityType',
  headerName: 'Entity Type',
  field: 'entityType',
  width: 120,
  filter: 'agTextColumnFilter'
};

export const allColumns = [
  colIsNew,
  colWorkflowNum,
  colName,
  colPhase,
  colStatus,
  colDocumentType,
  colTransactionType,
  colPriority,
  colRequestor,
  colAssignedTo,
  colDateRequested,
  colDaysInProcess,
  colCreatedDate,
  colDesiredEffectiveDate,
  colDateCompleted,
  colSourceRecord,
  colEntityType
];

export const columnSelectList = allColumns.map(col => ({
  value: col,
  viewValue: col.headerName
}));
